/**
 * Subsidiary Helper Functions
 * Centralized utilities for subsidiary information
 */

/**
 * Get subsidiary logo path
 * @param {string} subsidiary - Subsidiary code (e.g., 'puffin_group', 'buildwise')
 * @returns {string} - Path to logo image
 */
export const getSubsidiaryLogo = (subsidiary) => {
  const logos = {
    puffin_group: "/puffin_icon.png",
    buildwise: "/icons/buildwise.png",
    laser_shield: "/icons/laser_shield.png",
    maz_logistics: "/icons/maz_logistics.png",
    puffin_energy: "/icons/puffin_energy.png",
    swingtide: "/icons/swingtide.png",
    puffin_agro: "/icons/puffin_agro.png",
  };
  return logos[subsidiary] || "/puffin_icon.png";
};

/**
 * Get subsidiary display name
 * @param {string} subsidiary - Subsidiary code
 * @returns {string} - Full subsidiary name
 */
export const getSubsidiaryName = (subsidiary) => {
  const names = {
    puffin_group: "Puffin Group Ltd",
    buildwise: "Buildwise Construction Ltd",
    laser_shield: "Laser Shield Ltd.",
    maz_logistics: "Maz Logistics Ltd.",
    puffin_energy: "Puffin Energy Ltd.",
    swingtide: "Swingtide Consulting Ltd",
    puffin_agro: "Puffin Agro Allied",
  };
  return names[subsidiary] || "Puffin Group Ltd";
};

/**
 * Get subsidiary uppercase name (for invoices)
 * @param {string} subsidiary - Subsidiary code
 * @returns {string} - Uppercase subsidiary name
 */
export const getSubsidiaryNameUppercase = (subsidiary) => {
  const names = {
    puffin_group: "PUFFIN GROUP LTD",
    buildwise: "BUILDWISE CONSTRUCTION LTD",
    laser_shield: "LASER SHIELD LTD.",
    maz_logistics: "MAZ LOGISTICS LTD.",
    puffin_energy: "PUFFIN ENERGY LTD.",
    swingtide: "SWINGTIDE CONSULTING LTD",
    puffin_agro: "PUFFIN AGRO ALLIED",
  };
  return names[subsidiary] || "PUFFIN GROUP LTD";
};

/**
 * Get all subsidiaries
 * @returns {Array} - Array of subsidiary objects
 */
export const getAllSubsidiaries = () => {
  return [
    {
      code: "puffin_group",
      name: "Puffin Group Ltd",
      logo: "/puffin_icon.png",
    },
    {
      code: "buildwise",
      name: "Buildwise Construction Ltd",
      logo: "/icons/buildwise.png",
    },
    {
      code: "laser_shield",
      name: "Laser Shield Ltd.",
      logo: "/icons/laser_shield.png",
    },
    {
      code: "maz_logistics",
      name: "Maz Logistics Ltd.",
      logo: "/icons/maz_logistics.png",
    },
    {
      code: "puffin_energy",
      name: "Puffin Energy Ltd.",
      logo: "/icons/puffin_energy.png",
    },
    {
      code: "swingtide",
      name: "Swingtide Consulting Ltd",
      logo: "/icons/swingtide.png",
    },
    {
      code: "puffin_agro",
      name: "Puffin Agro Allied",
      logo: "/icons/puffin_agro.png",
    },
  ];
};

/**
 * Get subsidiary entity ID (for database)
 * @param {string} subsidiary - Subsidiary code
 * @returns {number} - Entity ID
 */
export const getSubsidiaryEntityId = (subsidiary) => {
  const entityIds = {
    puffin_group: 1,
    buildwise: 2,
    laser_shield: 3,
    maz_logistics: 4,
    puffin_energy: 5,
    swingtide: 6,
    puffin_agro: 7,
  };
  return entityIds[subsidiary] || 1;
};

/**
 * Get subsidiary code from entity ID
 * @param {number} entityId - Entity ID
 * @returns {string} - Subsidiary code
 */
export const getSubsidiaryCodeFromEntityId = (entityId) => {
  const codes = {
    1: "puffin_group",
    2: "buildwise",
    3: "laser_shield",
    4: "maz_logistics",
    5: "puffin_energy",
    6: "swingtide",
    7: "puffin_agro",
  };
  return codes[entityId] || "puffin_group";
};
